import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { clientService } from '@/services/client-service';
import type { Client } from '@/types/core/client';
import type { Page } from '@/types/shared/page';
import { pollUntilCondition } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { clientMutationKeys, clientQueryKeys } from './query-keys';

/**
 * Hook to revoke a client with optimistic updates.
 * Calls disableClient and waits until the read model reflects the revoked state,
 * then invalidates the cache.
 */
export function useRevokeClient(tenantId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: clientMutationKeys.revokeClient(tenantId),
    mutationFn: async (clientId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const success = await clientService.revokeClient(tenantId, clientId);
      if (!success) {
        throw new Error('Failed to revoke client');
      }

      await pollUntilCondition(async () => {
        const client = await clientService.findClient(tenantId, clientId);
        return client?.isRevoked === true || client?.enabled === false;
      });

      return clientId;
    },
    onMutate: async (clientId: UUID) => {
      if (!tenantId) return { previousData: [] };

      await qc.cancelQueries({
        queryKey: clientQueryKeys.listByTenant(tenantId),
      });

      const previousData = qc.getQueriesData<InfiniteData<Page<Client>>>({
        queryKey: clientQueryKeys.listByTenant(tenantId),
      });

      qc.setQueriesData<InfiniteData<Page<Client>>>(
        { queryKey: clientQueryKeys.listByTenant(tenantId) },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((c) =>
                c.clientId === clientId
                  ? { ...c, isRevoked: true, enabled: false }
                  : c
              ),
            })),
          };
        }
      );

      return { previousData };
    },
    onError: (_error, _clientId, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: clientQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
